<template>
  <div>
    <Dialog :open="openModal" @update:open="onClose">
      <DialogContent class="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Card</DialogTitle>
          <DialogDescription>Merchant Billing Debit Order</DialogDescription>
          <DialogDescription v-if="errorMessage" class="text-red-500">
            {{ errorMessage }}
          </DialogDescription>
        </DialogHeader>
        <SectionDivider />
        <div>
          <LayupSpinner v-if="!checkoutFormLoaded" />
          <div id="payment-form" :class="{ 'h-[550px]': checkoutFormLoaded }"></div>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BASE_URL, EMBEDDED_ENTITY_ID } from '@/config';
import { NetworkError } from '@/errors';
import { createCard, verifyCard } from '@/services/cards';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref, watch } from 'vue';
import type { ModelCardRequest } from '@layup/api-sdk';
import { waitForCheckoutSDK, waitForElement } from '../../../../utils/peach.utils';

const emit = defineEmits(['disable:modal', 'card:added']);
const props = defineProps<{
  shouldOpenModal: boolean;
}>();

const openModal = ref(false);
const errorMessage = ref('');
const checkoutFormLoaded = ref(false);

watch(
  () => props.shouldOpenModal,
  (newValue) => {
    openModal.value = newValue;
  },
  { immediate: true },
);

const loadCheckoutForm = async () => {
  try {
    const request: ModelCardRequest = {
      defaultCard: true,
      redirectUrl: `${BASE_URL}/merchant`,
    };

    const createdCard = await createCard(request);
    const { checkoutId } = createdCard;

    const sdkLoaded = await waitForCheckoutSDK();

    if (!sdkLoaded) {
      addMetric('add-card', { status: 'peach_sdk_load_failure', channel: 'merchant' });
      createException({
        message: 'Checkout SDK failed to load after multiple attempts.',
        checkoutId,
        userAgent: navigator.userAgent,
      });
      errorMessage.value =
        'Our payment provider failed to load. Please check your internet connection and try again. If the problem persists, please try a different browser.';
      return;
    }

    // The widget attaches to this element so we need to make sure it exists.
    await waitForElement('#payment-form');
    const checkout = await (window as any).Checkout.initiate({
      checkoutId,
      key: EMBEDDED_ENTITY_ID,
      events: {
        onCompleted: () =>
          verifyCard(checkoutId!).then(() => {
            emit('card:added');
            onClose();
          }),
        onCancelled: () => onClose(),
        onExpired: () => onClose(),
      },
      options: {
        theme: {
          brand: {
            primary: '#1295a5',
          },
        },
      },
    });
    checkout.render('#payment-form');
    checkoutFormLoaded.value = true;
    addMetric('add-card', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('add-card', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('add-card', { status: 'error', channel: 'merchant' });
    }
    errorMessage.value = `Error loading checkout form: ${error}`;
  }
};

const onClose = () => {
  openModal.value = false;
  emit('disable:modal', false);
};

const init = async () => {
  try {
    await loadCheckoutForm();
  } catch (error: any) {
    console.error('Error loading checkout form:', error);
    errorMessage.value = 'Error loading checkout form. Please try again later.';
    if (error?.response?.data) {
      errorMessage.value = error.response.data;
    }
  }
};
init();
</script>
